import { useState, useEffect } from 'react';
import { accountsAPI, wireAPI, fxAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Landmark } from 'lucide-react';
import { toast } from 'sonner';
import DollarInput from '../components/DollarInput';
import PreTransferConfirm from '../components/PreTransferConfirm';
import TransferConfirmation from '../components/TransferConfirmation';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'INR', 'MXN', 'BRL', 'SGD', 'HKD'];

export default function WirePage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(null);
  const [preConfirmOpen, setPreConfirmOpen] = useState(false);
  const [preConfirmDetails, setPreConfirmDetails] = useState(null);
  const [fromAccount, setFromAccount] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [beneficiaryBank, setBeneficiaryBank] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [swiftCode, setSwiftCode] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [purpose, setPurpose] = useState('');
  const [memo, setMemo] = useState('');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await accountsAPI.list();
      setAccounts(res.data.accounts);
      if (res.data.accounts.length > 0) setFromAccount(res.data.accounts[0]._id);
    } catch (err) {
      toast.error('Failed to load accounts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setPreConfirmDetails({
      type: 'Wire Transfer',
      amount: parseFloat(amount),
      fromAccount: selectedAccount?.name || 'Account',
      recipient: beneficiaryName,
      extraFields: [
        { label: 'Bank', value: beneficiaryBank || 'N/A' },
        { label: 'Currency', value: currency },
      ],
    });
    setPreConfirmOpen(true);
  };

  const executeWireTransfer = async () => {
    setLoading(true);
    try {
      const res = await wireAPI.send({
        from_account_id: fromAccount,
        beneficiary_name: beneficiaryName,
        beneficiary_bank: beneficiaryBank,
        routing_number: routingNumber,
        swift_code: swiftCode,
        amount: parseFloat(amount),
        currency,
        purpose,
        memo
      });
      setPreConfirmOpen(false);
      setConfirmDetails({
        type: 'Wire Transfer',
        amount: parseFloat(amount),
        recipient: beneficiaryName,
        fromAccount: selectedAccount?.name || 'Account',
        convertedAmount: currency !== 'USD' ? res.data.converted_amount : undefined,
        fxRate: currency !== 'USD' ? res.data.fx_rate : undefined,
        targetCurrency: currency !== 'USD' ? currency : undefined,
      });
      setConfirmOpen(true);
      setBeneficiaryName(''); setBeneficiaryBank(''); setRoutingNumber('');
      setSwiftCode(''); setAmount(''); setPurpose(''); setMemo('');
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wire transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = accounts.find(a => a._id === fromAccount);

  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Wire Transfer</h2>
      <Card className="max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Landmark className="h-5 w-5 text-[#0B5CAB]" />
            </div>
            <div>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Send Wire Transfer</CardTitle>
              <p className="text-sm text-muted-foreground">Domestic and international wire transfers</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>From Account</Label>
              <Select value={fromAccount} onValueChange={setFromAccount}>
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a._id} value={a._id}>{a.name} ({formatCurrency(a.available_balance)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAccount && <p className="text-xs text-muted-foreground">Available: {formatCurrency(selectedAccount.available_balance)}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Beneficiary Name</Label>
                <Input placeholder="Full legal name" value={beneficiaryName} onChange={e => setBeneficiaryName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Beneficiary Bank</Label>
                <Input placeholder="Bank name" value={beneficiaryBank} onChange={e => setBeneficiaryBank(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Routing Number</Label>
                <Input data-testid="wire-routing-input" placeholder="021000021" value={routingNumber} onChange={e => setRoutingNumber(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>SWIFT/BIC Code</Label>
                <Input data-testid="wire-swift-input" placeholder="CHASUS33" value={swiftCode} onChange={e => setSwiftCode(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <DollarInput value={amount} onChange={e => setAmount(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Input placeholder="Business payment, personal, etc." value={purpose} onChange={e => setPurpose(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Memo</Label>
              <Textarea placeholder="Additional notes" value={memo} onChange={e => setMemo(e.target.value)} />
            </div>
            <Button data-testid="wire-submit-button" type="submit" className="w-full" disabled={loading}>
              {loading ? 'Processing...' : 'Review Wire Transfer'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <PreTransferConfirm open={preConfirmOpen} onClose={setPreConfirmOpen} onConfirm={executeWireTransfer} details={preConfirmDetails} loading={loading} />
      <TransferConfirmation open={confirmOpen} onClose={setConfirmOpen} transferDetails={confirmDetails} />
    </div>
  );
}